import type { RequestProjectsParams } from '@/features/project/_hooks/project';
import {
  type FormulaAmountType,
  type FormulaTrendCondition,
  amountTypeToBackendMap,
  budgetTypeToBackendAmountTypeMap,
  operatorToTrendConditionMap,
} from '@/features/project/_models/formula-conditions';
import type { FormulaCondition } from '@/features/project/_models/formula-conditions';
import {
  TrendThresholdValueMap,
  amountDifferenceOperator,
} from '@/features/project/_models/project-filter';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import { getInitialValueOfOptionalFields } from '@/features/search/_utils/projects-search';
import {
  AmountUnitMultipliers,
  BigCalculator,
  isPS,
  stringToBoolean,
} from '@/utils';
import Big from 'big.js';
import type { z } from 'zod';
import type { ProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';

type ProjectFilterParams = {
  isDisplayPolicyLevel?: boolean;
  sorting: string;
  searchQueryStates: ProjectsPageSearchQueryStates;
  groupFiscalYear: string | undefined;
};
const SPLIT_STRING = ',';

export const convertRequestParams = ({
  isDisplayPolicyLevel,
  searchQueryStates,
  sorting,
  groupFiscalYear,
}: ProjectFilterParams): RequestProjectsParams => {
  const {
    sheetType,
    isCandidateOfInspectionByExternalExpert,
    isPublished,
    page,
    pageSize,
    startYear,
    endYear,
    ordering,
    categories,
    implementationMethods,
    inspectionByExternalExpertReviewType,
    isSearchTargetProjectName,
    accountingTypeIds,
    amountSearchBaseAmountType,
    amountSearchBaseBudgetType,
    amountSearchBaseTargetYear,
    amountSearchDiffAmount,
    amountSearchDiffAmountUnit,
    amountSearchDiffOperator,
    amountSearchFilterType,
    amountSearchRightAmountType,
    amountSearchRightBudgetType,
    amountSearchRightTargetYear,
    amountSearchType,
    cascader,
    accountingType,
    fundSheetType,
    corporateNumber,
    lastImplementedFiscalYearRangeMax,
    lastImplementedFiscalYearRangeMin,
    projectNumbers,
    organizationIds,
    policyIds,
    otherResponsibleOrganizationIds,
    expenditureBudgetPurposeIds,
    q,
    formula_conditions,
  } = searchQueryStates;

  const initialValueOfOptionalFields = getInitialValueOfOptionalFields({
    searchQueryStates,
    initialFiscalYearByGroup: groupFiscalYear,
  });

  const allParams: RequestProjectsParams = {
    sheetType,
    isCandidateOfInspectionByExternalExpert: stringToBoolean(
      isCandidateOfInspectionByExternalExpert,
    ),
    startYear: startYear?.toString() ?? null,
    endYear: endYear?.toString() ?? null,
    isPublished: stringToBoolean(isPublished),
    page: page.toString(),
    pageSize: pageSize.toString(),
    ordering: isDisplayPolicyLevel ? sorting : ordering,
    categories: categories?.join(SPLIT_STRING) ?? null,
    implementationMethods: implementationMethods?.join(SPLIT_STRING) ?? null,
    inspectionByExternalExpertReviewType:
      inspectionByExternalExpertReviewType?.join(SPLIT_STRING) ?? null,
    textSearchCriteria: isSearchTargetProjectName ? 'name' : null,
    accountingTypeIds: accountingTypeIds?.join(SPLIT_STRING) ?? null,
    fiscalYear: initialValueOfOptionalFields.fiscalYear,
    skipAdministrativeReformTeamEvaluation:
      initialValueOfOptionalFields.skipAdministrativeReformTeamEvaluation
        ? stringToBoolean(
            initialValueOfOptionalFields.skipAdministrativeReformTeamEvaluation,
          )
        : null,
    amountSearchBaseAmountType,
    amountSearchBaseBudgetType,
    amountSearchBaseTargetYear,
    amountSearchDiffAmount,
    amountSearchDiffAmountUnit,
    amountSearchDiffOperator,
    amountSearchFilterType,
    amountSearchRightAmountType,
    amountSearchRightBudgetType,
    amountSearchRightTargetYear,
    amountSearchType,
    cascader,
    accountingType,
    fundSheetType,
    corporateNumber,
    lastImplementedFiscalYearRangeMax,
    lastImplementedFiscalYearRangeMin,
    projectNumbers,
    organizationIds: organizationIds?.join(SPLIT_STRING) ?? null,
    policyIds: policyIds?.join(SPLIT_STRING) ?? null,
    otherResponsibleOrganizationIds:
      otherResponsibleOrganizationIds?.join(SPLIT_STRING) ?? null,
    expenditureBudgetPurposeIds:
      expenditureBudgetPurposeIds?.join(SPLIT_STRING) ?? null,
    q,
    formula_conditions: formula_conditions ?? null,
  };
  const formattedParams = getAnalysisRequestParams(allParams);

  // nullは除外
  return Object.fromEntries(
    Object.entries(formattedParams).filter(
      ([_, value]) => value !== null && value !== undefined,
    ),
  ) as RequestProjectsParams;
};

const getAnalysisRequestParams = (params: RequestProjectsParams) => {
  const isTrend = !!params.amountSearchRightTargetYear;
  const targets = [
    params.amountSearchBaseAmountType,
    params.amountSearchBaseBudgetType,
    params.amountSearchBaseTargetYear,
    params.amountSearchDiffAmount,
    params.amountSearchDiffAmountUnit,
    params.amountSearchDiffOperator,
    params.amountSearchFilterType,
    ...(isTrend ? [params.amountSearchRightAmountType] : []),
    ...(isTrend ? [params.amountSearchRightBudgetType] : []),
    ...(isTrend ? [params.amountSearchRightTargetYear] : []),
  ];
  const someNull = targets.some(
    (target) => target === null || target === undefined,
  );

  if (someNull) {
    return {
      ...params,
      amountSearchType: null,
      amountSearchBaseAmountType: null,
      amountSearchBaseBudgetType: null,
      amountSearchBaseTargetYear: null,
      amountSearchDiffAmount: null,
      amountSearchDiffAmountUnit: null,
      amountSearchDiffOperator: null,
      amountSearchFilterType: null,
      amountSearchRightAmountType: null,
      amountSearchRightBudgetType: null,
      amountSearchRightTargetYear: null,
    };
  }

  return params;
};

/**数式条件のリクエストパラメータを取得する（後方互換性のため残す） */
const getAnalyticRequestParams = (
  _values: ProjectSearchFormType,
  _isAnalyticRequest: boolean,
) => {
  // formula_conditionsを使用するため、個別パラメータは全てnullを返す
  return {
    amountSearchType: null,
    amountSearchDiffAmount: null,
    amountSearchDiffOperator: null,
    amountSearchDiffAmountUnit: null,
    amountSearchBaseBudgetType: null,
    amountSearchFilterType: null,
    amountSearchRightBudgetType: null,
    amountSearchBaseAmountType: null,
    amountSearchRightAmountType: null,
    amountSearchBaseTargetYear: null,
    amountSearchRightTargetYear: null,
  };
};

const getIsPsParams = ({
  nestedExpenditureBudgetPurposeIds,
  nestedPolicyIds,
  nestedOrganizationIds,
  nestedOtherResponsibleOrganizationIds,
  sheetType,
}: ProjectSearchFormType) => {
  if (!isPS()) {
    return {
      expenditureBudgetPurposeIds: null,
      policyIds: null,
      organizationIds: null,
      otherResponsibleOrganizationIds: null,
    };
  }
  const rsOnly = sheetType === 'RS';

  return {
    expenditureBudgetPurposeIds:
      rsOnly && nestedExpenditureBudgetPurposeIds?.length
        ? nestedExpenditureBudgetPurposeIds.flatMap((item) => item.at(-1) ?? [])
        : null,
    policyIds:
      rsOnly && nestedPolicyIds?.length
        ? nestedPolicyIds.flatMap((item) => item.at(-1) ?? [])
        : null,
    organizationIds: nestedOrganizationIds?.length
      ? nestedOrganizationIds?.flatMap((item) => item.at(-1) ?? [])
      : null,
    otherResponsibleOrganizationIds:
      nestedOtherResponsibleOrganizationIds?.length
        ? nestedOtherResponsibleOrganizationIds.flatMap(
            (item) => item.at(-1) ?? [],
          )
        : null,
  };
};

/**
 * バックエンドのformula_conditionsからフロントエンドのamountSearchConditions形式に変換する
 */
export const convertFormulaConditionsToAmountSearchConditions = (
  formulaConditionsJson: string | null | undefined,
): ProjectSearchFormType['amountSearchConditions'] => {
  if (!formulaConditionsJson) {
    return null;
  }

  try {
    const formulaConditions = JSON.parse(
      formulaConditionsJson,
    ) as FormulaCondition[];

    return formulaConditions.map((condition) => {
      const isThreshold = condition.condition_type === 'threshold';
      const isTrend = condition.condition_type === 'trend';

      // バックエンドの金額タイプからフロントエンドの形式を逆引き
      const findFrontendBudgetType = (
        backendAmountType: string,
      ): string | null => {
        // 直接的なマッピングを優先
        const directMapping: Record<string, string> = {
          requested_amount: 'allRequestedAmount',
          finalized_amount_for_initial_budget: 'initialFinalizedAmount',
          finalized_amount_for_supplementary_budget:
            'supplementaryFinalizedAmount',
          finalized_amount_for_carried_forward: 'carryOverFinalizedAmount',
          finalized_amount_for_reserve: 'reserveFinalizedAmount',
          finalized_amount: 'allFinalizedAmount',
          execution_amount: 'allExecutedAmount',
          execution_rate: 'executionRate',
        };

        if (directMapping[backendAmountType]) {
          return directMapping[backendAmountType];
        }

        // フォールバック: 既存のロジック
        for (const [budgetType, amountTypeMap] of Object.entries(
          budgetTypeToBackendAmountTypeMap,
        )) {
          for (const [frontendAmountType, backendType] of Object.entries(
            amountTypeMap,
          )) {
            if (backendType === backendAmountType) {
              for (const [key, value] of Object.entries(
                TrendThresholdValueMap,
              )) {
                if (
                  value.budgetType === budgetType &&
                  value.budgetAmount === frontendAmountType
                ) {
                  return key;
                }
              }
            }
          }
        }

        for (const [frontendType, backendType] of Object.entries(
          amountTypeToBackendMap,
        )) {
          if (backendType === backendAmountType) {
            for (const [key, value] of Object.entries(TrendThresholdValueMap)) {
              if (
                value.budgetType === 'all' &&
                value.budgetAmount === frontendType
              ) {
                return key;
              }
            }
          }
        }

        return null;
      };

      const baseBudgetTypeKey = findFrontendBudgetType(
        condition.base_amount_type,
      );
      const targetBudgetTypeKey = condition.target_amount_type
        ? findFrontendBudgetType(condition.target_amount_type)
        : null;

      // 金額の単位を千円単位に戻す
      let diffAmount: number | null = null;
      let diffAmountUnit: 'amount' | 'percent' = 'amount';
      let diffOperator: 'gte' | 'lte' | 'eq' | 'none' | null = null;

      if (isThreshold) {
        if (condition.base_amount_value_isnull) {
          diffOperator = 'none';
          diffAmount = null;
        } else if (
          condition.base_amount_value_from !== null &&
          condition.base_amount_value_to !== null &&
          condition.base_amount_value_from === condition.base_amount_value_to
        ) {
          diffOperator = 'eq';
          diffAmount =
            condition.base_amount_value_from / AmountUnitMultipliers.thousand;
        } else if (condition.base_amount_value_from !== null) {
          diffOperator = 'gte';
          diffAmount =
            condition.base_amount_value_from / AmountUnitMultipliers.thousand;
        } else if (condition.base_amount_value_to !== null) {
          diffOperator = 'lte';
          diffAmount =
            condition.base_amount_value_to / AmountUnitMultipliers.thousand;
        }
      } else if (isTrend && condition.trend_condition) {
        // トレンド条件の逆引き
        for (const [operator, unitMap] of Object.entries(
          operatorToTrendConditionMap,
        )) {
          for (const [unit, trendCondition] of Object.entries(unitMap)) {
            if (trendCondition === condition.trend_condition) {
              diffOperator = operator as 'gte' | 'lte' | 'eq' | 'none';
              diffAmountUnit = unit as 'amount' | 'percent';
              break;
            }
          }
          if (diffOperator) {
            break;
          }
        }

        if (condition.trend_condition_value !== null) {
          diffAmount =
            diffAmountUnit === 'amount'
              ? condition.trend_condition_value / AmountUnitMultipliers.thousand
              : condition.trend_condition_value;
        }
      }

      return {
        amountSearchType: isThreshold ? 'threshold' : isTrend ? 'trend' : null,
        amountSearchDiffAmount: diffAmount,
        amountSearchDiffAmountUnit: diffAmountUnit,
        amountSearchDiffOperator: diffOperator,
        amountSearchBaseYearWithBudgetType: baseBudgetTypeKey
          ? [String(condition.base_amount_year), baseBudgetTypeKey]
          : null,
        amountSearchBaseYearWithBudgetTypeEnd:
          isTrend && targetBudgetTypeKey && condition.target_amount_year
            ? [String(condition.target_amount_year), targetBudgetTypeKey]
            : null,
      };
    });
  } catch (_error) {
    return null;
  }
};

/**
 * フロントエンドのamountSearchConditionsをバックエンドのformula_conditions形式に変換する
 */
const convertAmountSearchConditionsToFormulaConditions = (
  amountSearchConditions: ProjectSearchFormType['amountSearchConditions'],
): FormulaCondition[] | null => {
  if (!amountSearchConditions || amountSearchConditions.length === 0) {
    return null;
  }

  const validConditions = amountSearchConditions
    .filter((condition) => {
      // 必須フィールドが設定されているかチェック
      return (
        condition.amountSearchType &&
        condition.amountSearchBaseYearWithBudgetType &&
        condition.amountSearchBaseYearWithBudgetType.length >= 2
      );
    })
    .map((condition) => {
      const {
        amountSearchType,
        amountSearchDiffAmount,
        amountSearchDiffAmountUnit,
        amountSearchDiffOperator,
        amountSearchBaseYearWithBudgetType,
        amountSearchBaseYearWithBudgetTypeEnd,
      } = condition;

      const [baseYear, baseBudgetTypeWithAmount] =
        amountSearchBaseYearWithBudgetType ?? [];
      const [targetYear, targetBudgetTypeWithAmount] =
        amountSearchBaseYearWithBudgetTypeEnd ?? [];

      // 基本の金額タイプと予算タイプを取得
      const baseBudgetInfo =
        TrendThresholdValueMap[
          baseBudgetTypeWithAmount as keyof typeof TrendThresholdValueMap
        ];
      const targetBudgetInfo = targetBudgetTypeWithAmount
        ? TrendThresholdValueMap[
            targetBudgetTypeWithAmount as keyof typeof TrendThresholdValueMap
          ]
        : null;

      // バックエンドの金額タイプ形式に変換
      const getBackendAmountType = (
        budgetType: string,
        amountType: string,
      ): string => {
        const specificType =
          budgetTypeToBackendAmountTypeMap[budgetType]?.[amountType];
        if (specificType) {
          return specificType;
        }
        return amountTypeToBackendMap[amountType] || amountType;
      };

      const baseAmountType = getBackendAmountType(
        baseBudgetInfo.budgetType,
        baseBudgetInfo.budgetAmount,
      );
      const targetAmountType = targetBudgetInfo
        ? getBackendAmountType(
            targetBudgetInfo.budgetType,
            targetBudgetInfo.budgetAmount,
          )
        : null;

      // 差分金額の処理
      let processedDiffAmount = amountSearchDiffAmount;
      const isNoneOperator =
        amountSearchDiffOperator === amountDifferenceOperator.Values.none;

      if (isNoneOperator) {
        processedDiffAmount = null;
      } else if (
        typeof amountSearchDiffAmount === 'number' &&
        amountSearchDiffAmountUnit === 'amount'
      ) {
        processedDiffAmount = Big(
          BigCalculator.multiply(
            amountSearchDiffAmount,
            AmountUnitMultipliers.thousand,
          ),
        )
          .round()
          .toNumber();
      }

      const isThreshold = amountSearchType === 'threshold';
      const isTrend = amountSearchType === 'trend';

      // 閾値条件の値を決定
      const getThresholdValues = () => {
        if (!isThreshold) {
          return {
            base_amount_value_from: null,
            base_amount_value_to: null,
            base_amount_value_isnull: false,
          };
        }

        if (isNoneOperator) {
          return {
            base_amount_value_from: null,
            base_amount_value_to: null,
            base_amount_value_isnull: true,
          };
        }

        switch (amountSearchDiffOperator) {
          case 'eq':
            return {
              base_amount_value_from: processedDiffAmount,
              base_amount_value_to: processedDiffAmount,
              base_amount_value_isnull: false,
            };
          case 'gte':
            return {
              base_amount_value_from: processedDiffAmount,
              base_amount_value_to: null,
              base_amount_value_isnull: false,
            };
          case 'lte':
            return {
              base_amount_value_from: null,
              base_amount_value_to: processedDiffAmount,
              base_amount_value_isnull: false,
            };
          default:
            return {
              base_amount_value_from: null,
              base_amount_value_to: null,
              base_amount_value_isnull: false,
            };
        }
      };

      // トレンド条件の値を決定
      const getTrendValues = () => {
        if (
          !isTrend ||
          !targetYear ||
          !targetAmountType ||
          !amountSearchDiffOperator
        ) {
          return {
            target_amount_year: null,
            target_amount_type: null,
            trend_condition: null,
            trend_condition_value: null,
          };
        }

        const trendCondition =
          operatorToTrendConditionMap[amountSearchDiffOperator]?.[
            amountSearchDiffAmountUnit || 'amount'
          ];

        return {
          target_amount_year: Number(targetYear),
          target_amount_type: targetAmountType as z.infer<
            typeof FormulaAmountType
          > | null,
          trend_condition: trendCondition as z.infer<
            typeof FormulaTrendCondition
          > | null,
          trend_condition_value: trendCondition ? processedDiffAmount : null,
        };
      };

      // 条件オブジェクトを組み立て
      const formulaCondition: FormulaCondition = {
        condition_type: isThreshold ? 'threshold' : 'trend',
        base_amount_year: Number(baseYear),
        base_amount_type: baseAmountType as z.infer<typeof FormulaAmountType>,
        ...getThresholdValues(),
        ...getTrendValues(),
      };

      return formulaCondition;
    });

  return validConditions.length > 0 ? validConditions : null;
};

export const convertInputValueToRequestParams = (
  values: ProjectSearchFormType,
  newCascadeId: string | undefined | null,
): ProjectsPageSearchQueryStates => {
  const { sheetType, fundSheetType, period, lastImplementedFiscalYearPeriod } =
    values;

  const fiscalYear = values.fiscalYear?.year() ?? null;
  const hasFiscalYear = fiscalYear !== null;

  const rsOnly = sheetType === 'RS';
  const ssOnly = sheetType === 'SS';
  const ksOnly = sheetType === 'KS';

  const withoutKs = sheetType !== 'KS';

  const isReviewTargetRs =
    rsOnly && values.skipAdministrativeReformTeamEvaluation !== 'true';

  const standardKs = ksOnly && fundSheetType === 'standard';
  const localGovernmentKs = ksOnly && fundSheetType === 'local-government';

  const analyticRequestParams = getAnalyticRequestParams(
    values,
    rsOnly && hasFiscalYear,
  );

  // formula_conditionsを生成
  const formulaConditions =
    rsOnly && hasFiscalYear
      ? convertAmountSearchConditionsToFormulaConditions(
          values.amountSearchConditions,
        )
      : null;

  return {
    page: 1,
    pageSize: 30,
    sheetType,
    fiscalYear: fiscalYear?.toString() ?? null,
    fundSheetType: ksOnly ? fundSheetType : null,
    q: values.q ?? null,
    isSearchTargetProjectName: (values.q
      ? (values.isSearchTargetProjectName ?? false)
      : null) as any,
    cascader: newCascadeId ?? null,
    corporateNumber:
      standardKs || isReviewTargetRs || ssOnly ? values.corporateNumber : null,
    categories:
      withoutKs && values.categories?.length ? values.categories : null,
    implementationMethods:
      isReviewTargetRs && values.implementationMethods?.length
        ? values.implementationMethods
        : null,
    accountingType: rsOnly ? (values.accountingType ?? null) : null,
    accountingTypeIds:
      rsOnly && values.accountingTypeIds?.length
        ? values.accountingTypeIds
        : null,
    skipAdministrativeReformTeamEvaluation: (rsOnly && !isPS()
      ? values.skipAdministrativeReformTeamEvaluation === null
        ? 'all'
        : values.skipAdministrativeReformTeamEvaluation
      : null) as any,
    projectNumbers: values.projectNumbers ?? null,
    isPublished: !isPS() ? values.isPublished : null,
    startYear: localGovernmentKs ? null : (period?.[0]?.year() ?? null),
    endYear: localGovernmentKs ? null : (period?.[1]?.year() ?? null),
    lastImplementedFiscalYearRangeMin:
      isReviewTargetRs && hasFiscalYear
        ? (lastImplementedFiscalYearPeriod?.[0]?.year() ?? null)
        : null,
    lastImplementedFiscalYearRangeMax:
      isReviewTargetRs && hasFiscalYear
        ? (lastImplementedFiscalYearPeriod?.[1]?.year() ?? null)
        : null,
    inspectionByExternalExpertReviewType: isReviewTargetRs
      ? values.inspectionByExternalExpertReviewType
      : null,
    isCandidateOfInspectionByExternalExpert:
      isReviewTargetRs && !isPS()
        ? (values.isCandidateOfInspectionByExternalExpert ?? null)
        : null,
    ordering: null,
    ...analyticRequestParams,
    ...getIsPsParams(values),
    // formula_conditionsをJSON文字列として追加
    formula_conditions: formulaConditions
      ? JSON.stringify(formulaConditions)
      : null,
    // group
    groupInProjectsOrdering: null,
    groupInProjectsPage: 1,
  };
};
